/**
 * Registers Cmd+Shift+L / Ctrl+Shift+L to lock. Only locks when device access mode is pin/switch and not impersonating.
 */

import { useEffect } from "react";
import { useAccountContext } from "@/state/providers/AccountProvider";
import { useImpersonation } from "@/state/providers/ImpersonationProvider";
import { useLock } from "@/state/providers/LockProvider";
import { useDeviceAccessSettings } from "@/state/hooks/useDeviceAccess";

export function useLockShortcutListener() {
    const { accountData } = useAccountContext();
    const { requestLock } = useLock();
    const { isImpersonating } = useImpersonation();
    const { data: deviceSettings } = useDeviceAccessSettings();

    const pinsEnabled = accountData?.pins_enabled === true;

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (!pinsEnabled) return;
            const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.platform);
            const mod = isMac ? e.metaKey : e.ctrlKey;
            if (mod && e.shiftKey && e.key.toLowerCase() === "l") {
                if (isImpersonating) return;
                const mode = deviceSettings?.device_access_mode;
                if (mode === "pin" || mode === "switch") {
                    e.preventDefault();
                    e.stopPropagation();
                    requestLock();
                }
            }
        };
        // Use capture so we run before focused inputs/editors can consume the key
        window.addEventListener("keydown", onKeyDown, true);
        return () => window.removeEventListener("keydown", onKeyDown, true);
    }, [pinsEnabled, requestLock, isImpersonating, deviceSettings?.device_access_mode]);
}
